/**
 * Block 12 calculator — `facadeCount`: количество фасадов (штук).
 *
 * 3 anketa variants (Кухня, Шкаф, Балкон) — остальные 4 (Тумба-ванна,
 * Антресоль, Шкаф-санузел, ТВ-зона) принимают handle/hinge как user input
 * (per методике §В.1) и не имеют автоматической формулы.
 *
 * **Округление:**
 * - Кухня — `Math.round` (per OQ-1 ответ Aibek)
 * - Шкаф / Балкон — `ceilToEven` (per OQ-5)
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.X В.1 / §4.6.
 */

import type { CalculatorParams } from '../../contracts/calculator';
import { ceilToEven } from '../utils/ceilToEven';
import { mmToM } from '../utils/mmToM';
import {
  assertPositiveDimension,
  type BalconyParams,
  type CabinetParams,
  type KitchenParams,
} from './types';

/** Минимальная высота кухни (мм) — высота цоколя. Per OQ-D. */
const KITCHEN_MIN_HEIGHT_MM = 600;
/** Кухня adjustments — штук фасадов. */
const KITCHEN_FACADE_ADJ_PENAL = 2;
const KITCHEN_FACADE_ADJ_SHELF_ABOVE_FRIDGE = 1;

/**
 * Кухня прямая: `(L_м/0.5) × ((H_м-0.6)/0.8) − 1 + adjustments`.
 * Кухня угловая: `((L_м+L2_м-0.6)/0.5) × ((H_м-0.6)/0.8) − 1 + adjustments`.
 *
 * Adjustments (штук):
 * - `hasShelfAboveFridge=true` → `+1`
 * - `hasPenal=true` → `+2`
 *
 * **Order matters per вопрос C:** сначала суммируем base + adjustments,
 * потом ОДИН раз округляем через `Math.round` (не пер-операнд!).
 *
 * **Округление: `Math.round`** per OQ-1 ответ Aibek (стандартное к ближайшему).
 *
 * ⚠️ **xlsx error (B-097):** `Кухня!C45` пишет «27 фасадов» для L=5м, H=2.9м,
 *    но Aibek подтвердил `Math.round(27.75) = 28`. Эта функция реализует
 *    **правило**, а не xlsx-пример.
 *
 * **Источник:** xlsx `Кухня!C45` (прямая), `D45` (угловая) + OQ-1.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.1 В.1 + §4.6.
 *
 * @throws Error если `H < 600` (per OQ-D), `L = 0`, или угловая без `length2`.
 * @example
 *   Кухня прямая L=5000, H=2900, no penal/shelf:
 *     (5/0.5) × ((2.9-0.6)/0.8) − 1 = 10 × 2.875 − 1 = 27.75 → round = 28
 *   Кухня угловая L=3000, L2=2000, H=2900:
 *     ((3+2-0.6)/0.5) × ((2.9-0.6)/0.8) − 1 = 8.8 × 2.875 − 1 = 24.3 → round = 24
 */
export function facadeCountKitchen(params: KitchenParams): number {
  assertPositiveDimension(params.length, 'facadeCountKitchen.length');
  assertPositiveDimension(params.height, 'facadeCountKitchen.height');
  if (params.height < KITCHEN_MIN_HEIGHT_MM) {
    throw new Error(
      `facadeCountKitchen: height must be ≥ ${KITCHEN_MIN_HEIGHT_MM}mm, got ${params.height} (per OQ-D)`,
    );
  }

  const Hm = mmToM(params.height);
  let totalLm: number;
  if (params.isAngular) {
    assertPositiveDimension(params.length2, 'facadeCountKitchen.length2 (isAngular=true)');
    totalLm = mmToM(params.length) + mmToM(params.length2) - 0.6;
    if (totalLm <= 0) {
      throw new Error(
        `facadeCountKitchen: angular L+L2 must be > 600mm, got L=${params.length}, L2=${params.length2}`,
      );
    }
  } else {
    totalLm = mmToM(params.length);
  }

  const base = (totalLm / 0.5) * ((Hm - 0.6) / 0.8) - 1;
  const adjustments =
    (params.hasShelfAboveFridge ? KITCHEN_FACADE_ADJ_SHELF_ABOVE_FRIDGE : 0) +
    (params.hasPenal ? KITCHEN_FACADE_ADJ_PENAL : 0);
  return Math.round(base + adjustments);
}

/**
 * Шкаф / Гардероб:
 * - прямой: `(L_м/0.5) × 2`, округление `ceilToEven`
 * - угловой: `((L_м+L2_м-0.6)/0.5) × 2`, округление `ceilToEven`
 *
 * `isBuiltIn` НЕ влияет на facadeCount (formula зависит только от type).
 *
 * **Источник:** xlsx `Шкаф!C36` (прямой), `D36` (угловой) + OQ-5.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.2 В.1 + §4.6.
 *
 * @throws Error если угловой и `L+L2 ≤ 600`.
 * @example
 *   прямой L=2000 → (2/0.5) × 2 = 8 → ceilToEven = 8
 *   угловой L=3000, L2=2000 → ((3+2-0.6)/0.5) × 2 = 17.6 → ceilToEven = 18
 */
export function facadeCountCabinet(params: CabinetParams): number {
  assertPositiveDimension(params.length, 'facadeCountCabinet.length');

  let totalLm: number;
  if (params.isAngular) {
    assertPositiveDimension(params.length2, 'facadeCountCabinet.length2 (isAngular=true)');
    totalLm = mmToM(params.length) + mmToM(params.length2) - 0.6;
    if (totalLm <= 0) {
      throw new Error(
        `facadeCountCabinet: angular L+L2 must be > 600mm, got L=${params.length}, L2=${params.length2}`,
      );
    }
  } else {
    totalLm = mmToM(params.length);
  }

  return ceilToEven((totalLm / 0.5) * 2);
}

/**
 * Балкон: формула идентична Шкафу (per методике §2.7 В.1):
 * - прямой: `(L_м/0.5) × 2`
 * - угловой: `((L_м+L2_м-0.6)/0.5) × 2`
 *
 * Угловой определяется по наличию `length2`. Округление: `ceilToEven`.
 *
 * **Источник:** xlsx `Балкон!C19` (прямой), `D19` (угловой) + OQ-5.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.7 В.1 + §4.6.
 *
 * @throws Error если угловой и `L+L2 ≤ 600`.
 */
export function facadeCountBalcony(params: BalconyParams): number {
  assertPositiveDimension(params.length, 'facadeCountBalcony.length');

  const isAngular = typeof params.length2 === 'number';
  let totalLm: number;
  if (isAngular) {
    assertPositiveDimension(params.length2, 'facadeCountBalcony.length2');
    totalLm = mmToM(params.length) + mmToM(params.length2) - 0.6;
    if (totalLm <= 0) {
      throw new Error(
        `facadeCountBalcony: angular L+L2 must be > 600mm, got L=${params.length}, L2=${params.length2}`,
      );
    }
  } else {
    totalLm = mmToM(params.length);
  }

  return ceilToEven((totalLm / 0.5) * 2);
}

/**
 * Top-level dispatcher: возвращает facadeCount для anketa с автоматической
 * формулой, throws для anketa с user-input handle/hinge.
 *
 * **Анкеты без автоматической формулы** (handle user input):
 * - `'bath-tabletop'`, `'bath-shelf-above'`, `'bath-cabinet'`, `'tv-zone'`
 * - `'other'`
 */
export function facadeCount(params: CalculatorParams): number {
  switch (params.type) {
    case 'kitchen':
      return facadeCountKitchen(params);
    case 'cabinet':
      return facadeCountCabinet(params);
    case 'balcony':
      return facadeCountBalcony(params);
    case 'bath-tabletop':
    case 'bath-shelf-above':
    case 'bath-cabinet':
    case 'tv-zone':
      throw new Error(
        `facadeCount: не применяется к "${params.type}" (handle/hinge — user input per методике §В.1)`,
      );
    case 'other':
      throw new Error('facadeCount: не применяется к "Другая мебель" (свободный режим)');
  }
}
